import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { API_CONFIG } from "../../Config/config-env.js";
import { applyExternalDarkMode } from "../../Config/functions.js";

/*
  Pagina «Nueva solicitud de cotizacion» (tenants ElevaX, Auren): formulario que INICIA el flujo
  flowAurenCotizacion. Se muestra dentro de xFlow como pagina del menu (iframe EXTERNAL) y usa el
  token del usuario que viene en la query (?token=...). No toca la tienda.

  Query: token, officecode (opcional; si no viene se elige en el formulario), darkmode.
  Llama a PUT /engine-users/init-flow con {flowcode, officecode, params:[{var, value}]}.
*/
const FLOWCODE = "flowAurenCotizacion";
const OFFICES = [
  { code: "T01", name: "Matriz" },
  { code: "T02", name: "R Plaza Salaverry" },
  { code: "T03", name: "Wong 2 de Mayo" },
  { code: "A01", name: "R Plaza Arequipa" },
];
const EMPTY = { cliente_nombre: "", empresa: "", cliente_telefono: "", cliente_email: "", items: "", notas: "" };

const CSS = `
.oxf{--bg:#f6f7f9;--card:#fff;--ink:#1c2430;--mut:#667085;--line:#e4e7ec;--ok:#0f7b4d;--danger:#b42318;--acc:#2b5fd9;
  font-family:-apple-system,Segoe UI,Roboto,Helvetica,Arial,sans-serif;color:var(--ink);background:var(--bg);padding:12px;min-height:100vh;box-sizing:border-box}
.xflow-external-dark .oxf{--bg:#111827;--card:#1b2433;--ink:#e6eaf2;--mut:#9aa6b8;--line:#2c3a50;--acc:#7aa2ff}
.oxf h2{margin:0 0 4px;font-size:20px} .oxf .muted{color:var(--mut);font-size:13px}
.oxf .card{background:var(--card);border:1px solid var(--line);border-radius:12px;padding:14px;margin:12px 0}
.oxf .grid{display:grid;grid-template-columns:repeat(auto-fit,minmax(220px,1fr));gap:12px}
.oxf label{display:block;font-size:13px;font-weight:600;margin-bottom:4px}
.oxf input,.oxf select,.oxf textarea{width:100%;box-sizing:border-box;padding:9px 10px;border:1px solid var(--line);border-radius:8px;background:var(--bg);color:var(--ink);font:inherit}
.oxf textarea{min-height:96px;resize:vertical}
.oxf .full{grid-column:1/-1}
.oxf button.go{display:inline-flex;align-items:center;gap:8px;padding:11px 18px;border:0;border-radius:10px;background:var(--ok);color:#fff;font-weight:700;font-size:15px;cursor:pointer}
.oxf button.go:disabled{opacity:.6;cursor:default}
.oxf .spin{width:15px;height:15px;border:2px solid rgba(255,255,255,.5);border-top-color:#fff;border-radius:50%;animation:oxs .8s linear infinite}
@keyframes oxs{to{transform:rotate(360deg)}}
.oxf .msg{padding:10px 12px;border-radius:10px;font-size:14px;margin-bottom:10px}
.oxf .msg.err{background:#fef3f2;color:var(--danger);border:1px solid #fecdca}
.oxf .msg.okm{background:#ecfdf3;color:var(--ok);border:1px solid #abefc6}
`;

export default function ElevaxNuevaCotizacion(props) {
  const search = (props.location && props.location.search) || window.location.search;
  const query = useMemo(() => new URLSearchParams(search), [search]);
  const token = query.get("token");
  const qOffice = query.get("officecode");
  const initialOffice = OFFICES.some((o) => o.code === qOffice) ? qOffice : "T01";

  const [vals, setVals] = useState(EMPTY);
  const [office, setOffice] = useState(initialOffice);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");
  const [created, setCreated] = useState(false);

  useEffect(() => {
    applyExternalDarkMode(search);
  }, [search]);

  const set = (name) => (e) => setVals({ ...vals, [name]: e.target.value });

  const submit = (e) => {
    e.preventDefault();
    setError("");
    if (!vals.cliente_nombre.trim() || !vals.cliente_telefono.trim() || !vals.items.trim()) {
      setError("Completa cliente, teléfono y productos.");
      return;
    }
    const params = Object.keys(vals)
      .filter((k) => String(vals[k]).trim() !== "")
      .map((k) => ({ var: k, value: String(vals[k]).trim() }));
    setSending(true);
    axios({
      method: "put",
      url: API_CONFIG.xflowurl + "/engine-users/init-flow",
      headers: { "Content-Type": "application/json", Authorization: "Bearer " + token },
      data: { flowcode: FLOWCODE, officecode: office, params },
    })
      .then((r) => r.data)
      .then((data) => {
        if (data && data.status) {
          setCreated(true);
          setVals(EMPTY);
          try {
            window.parent.postMessage("TAREA_COMPLETADA", "*");
          } catch (err) {
            /* sin padre */
          }
        } else {
          setError((data && (data.message_es || data.message)) || "No se pudo registrar la solicitud.");
        }
      })
      .catch(() => setError("Error de conexión al registrar la solicitud. Intenta de nuevo."))
      .finally(() => setSending(false));
  };

  return (
    <div className="oxf">
      <style>{CSS}</style>
      <h2>Nueva solicitud de cotización</h2>
      <div className="muted">Registra lo que pide el cliente. Se crea la solicitud y pasa a «Preparar cotización».</div>
      {created && (
        <div className="msg okm" style={{ marginTop: 12 }}>
          Solicitud registrada. Ya aparece en «Preparar cotización». Puedes registrar otra.
        </div>
      )}
      {error && <div className="msg err" style={{ marginTop: 12 }}>{error}</div>}
      <form className="card" onSubmit={submit}>
        <div className="grid">
          <div>
            <label>Cliente *</label>
            <input value={vals.cliente_nombre} onChange={set("cliente_nombre")} placeholder="Nombre del cliente" disabled={sending} />
          </div>
          <div>
            <label>Empresa</label>
            <input value={vals.empresa} onChange={set("empresa")} placeholder="Opcional" disabled={sending} />
          </div>
          <div>
            <label>Teléfono *</label>
            <input type="tel" value={vals.cliente_telefono} onChange={set("cliente_telefono")} disabled={sending} />
          </div>
          <div>
            <label>Email</label>
            <input type="email" value={vals.cliente_email} onChange={set("cliente_email")} disabled={sending} />
          </div>
          <div>
            <label>Tienda / oficina</label>
            <select value={office} onChange={(e) => setOffice(e.target.value)} disabled={sending}>
              {OFFICES.map((o) => (
                <option key={o.code} value={o.code}>{o.code} - {o.name}</option>
              ))}
            </select>
          </div>
          <div className="full">
            <label>Productos y cantidades *</label>
            <textarea value={vals.items} onChange={set("items")} placeholder="Una línea por producto. Ej: Remera DRY2 Pro talla M x 20" disabled={sending} />
          </div>
          <div className="full">
            <label>Notas</label>
            <textarea value={vals.notas} onChange={set("notas")} placeholder="Plazo, logo, lugar de entrega…" disabled={sending} />
          </div>
        </div>
        <div style={{ marginTop: 14 }}>
          <button className="go" type="submit" disabled={sending}>
            {sending && <span className="spin" />}
            {sending ? "Registrando solicitud… no cierres esta ventana." : "Registrar solicitud"}
          </button>
        </div>
      </form>
    </div>
  );
}
